/**
 * Accessibility patches for third-party app output that we cannot edit directly.
 *
 * 1. The Customer Fields app renders address inputs with combobox semantics
 *    (role="combobox", aria-expanded, aria-autocomplete, aria-controls) even when
 *    there is no live suggestions popup attached. Screen readers then announce
 *    the input as an interactive combobox that doesn't behave like one, which is
 *    worse than treating it as a plain text field. Strip the combobox semantics
 *    so the field is announced as a regular text input.
 *
 * 2. App-rendered toggles expose native checkbox/radio inputs with an explicit
 *    aria-checked attribute. ARIA in HTML forbids aria-checked on input[type=checkbox]
 *    and input[type=radio] because the native `checked` state is authoritative;
 *    the redundant attribute is flagged by validators and can desync from the
 *    real state. Remove aria-checked and let the native state stand.
 */
const COMBOBOX_SELECTOR = 'input[data-cf-input][role="combobox"]';
const ARIA_CHECKED_SELECTOR =
  'input[type="checkbox"][aria-checked], input[type="radio"][aria-checked]';

const fixCombobox = (input) => {
  input.removeAttribute('role');
  input.removeAttribute('aria-expanded');
  input.removeAttribute('aria-autocomplete');
  input.removeAttribute('aria-controls');
};

const fixAriaChecked = (input) => {
  input.removeAttribute('aria-checked');
};

const patch = (root) => {
  if (root.matches?.(COMBOBOX_SELECTOR)) fixCombobox(root);
  if (root.matches?.(ARIA_CHECKED_SELECTOR)) fixAriaChecked(root);
  root.querySelectorAll?.(COMBOBOX_SELECTOR).forEach(fixCombobox);
  root.querySelectorAll?.(ARIA_CHECKED_SELECTOR).forEach(fixAriaChecked);
};

const init = () => {
  patch(document);

  // Apps inject their inputs after page load, so watch for late additions.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        patch(node);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
